/**
 * Calls to the JSON API.
 *
 * The session lives in an HttpOnly cookie, so there is no token to attach or
 * store — `credentials: same-origin` is the whole of the auth story here, and
 * JavaScript never holds anything worth stealing.
 */

import type {
  Account,
  Group,
  GroupKind,
  Invite,
  Role,
  Task,
  TaskCategory,
  TaskLink,
  TaskPriority,
  TaskStatus,
} from '@virtual-office/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: init.body ? { 'content-type': 'application/json' } : {},
    ...init,
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // A proxy error page, or an empty body — fall through to the status.
  }

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${response.status})`;
    throw new ApiError(message, response.status);
  }
  return payload as T;
}

const post = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) });

/* --- session -------------------------------------------------------------- */

export const getMe = (): Promise<{ account: Account | null; needsBootstrap: boolean }> =>
  request('/api/auth/me');

export const login = (email: string, password: string): Promise<{ account: Account }> =>
  post('/api/auth/login', { email, password });

export const logout = (): Promise<{ ok: true }> => post('/api/auth/logout', {});

/* --- invites -------------------------------------------------------------- */

export const checkInvite = (token: string): Promise<{ email: string; role: Role }> =>
  request(`/api/invites/check?token=${encodeURIComponent(token)}`);

export const acceptInvite = (
  token: string,
  name: string,
  password: string,
): Promise<{ account: Account }> => post('/api/invites/accept', { token, name, password });

/* --- your own profile ------------------------------------------------------ */

export const uploadAvatar = (image: string): Promise<{ avatarVersion: number }> =>
  post('/api/profile/avatar', { image });

export const removeAvatar = (): Promise<{ avatarVersion: number }> =>
  post('/api/profile/avatar/remove', {});

/* --- groups ---------------------------------------------------------------- */

export const listGroups = (): Promise<{ groups: Group[]; accounts: Account[] }> =>
  request('/api/groups');

export const createGroup = (input: {
  name: string;
  kind: GroupKind;
  scope: 'global' | 'personal';
}): Promise<{ group: Group }> => post('/api/groups', input);

export const renameGroup = (id: string, name: string): Promise<{ ok: true }> =>
  post('/api/groups/rename', { id, name });

export const deleteGroup = (id: string): Promise<{ ok: true }> =>
  post('/api/groups/delete', { id });

export const setGroupMember = (
  id: string,
  userId: string,
  member: boolean,
): Promise<{ ok: true }> => post('/api/groups/members', { id, userId, member });

/* --- tasks ----------------------------------------------------------------- */

export const listTasks = (): Promise<{ tasks: Task[]; categories: TaskCategory[] }> =>
  request('/api/tasks');

export const listCategories = (): Promise<{ categories: TaskCategory[] }> =>
  request('/api/tasks/categories');

export const createCategory = (name: string): Promise<{ categories: TaskCategory[] }> =>
  post('/api/admin/tasks/categories', { name });

export const renameCategory = (
  id: string,
  name: string,
): Promise<{ categories: TaskCategory[] }> =>
  post('/api/admin/tasks/categories/rename', { id, name });

export const archiveCategory = (
  id: string,
  archived: boolean,
): Promise<{ categories: TaskCategory[] }> =>
  post('/api/admin/tasks/categories/archive', { id, archived });

export const deleteCategory = (id: string): Promise<{ categories: TaskCategory[] }> =>
  post('/api/admin/tasks/categories/delete', { id });

/**
 * Fields a task can be created or edited with.
 *
 * Every key optional and absent-means-untouched, matching the server: two
 * people editing different fields of the same task do not clobber each other.
 */
export interface TaskDraft {
  title?: string;
  description?: string;
  categoryId?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeId?: string | null;
  approverId?: string | null;
  groupId?: string | null;
  dueAt?: number | null;
  links?: TaskLink[];
  dependsOn?: string[];
}

export const createTask = (draft: TaskDraft): Promise<{ task: Task }> =>
  post('/api/tasks/create', draft);

export const updateTask = (id: string, draft: TaskDraft): Promise<{ task: Task }> =>
  post('/api/tasks/update', { id, ...draft });

export const approveTask = (id: string): Promise<{ task: Task }> =>
  post('/api/tasks/approve', { id });

export const deleteTask = (id: string): Promise<{ ok: true }> =>
  post('/api/tasks/delete', { id });

export const setDependency = (
  id: string,
  dependsOn: string,
  remove = false,
): Promise<{ ok: true }> => post('/api/tasks/depend', { id, dependsOn, remove });

/* --- admin ---------------------------------------------------------------- */

export const listInvites = (): Promise<{ invites: Invite[]; emailEnabled: boolean }> =>
  request('/api/admin/invites');

export const sendInvite = (
  email: string,
  role: Role,
  groupIds: string[] = [],
): Promise<{ invite: Invite; emailed: boolean }> =>
  post('/api/admin/invites', { email, role, groupIds });

export const revokeInvite = (id: string): Promise<{ ok: true }> =>
  post('/api/admin/invites/revoke', { id });

export const listAccounts = (): Promise<{ accounts: Account[] }> => request('/api/admin/accounts');

export const setAccountRole = (userId: string, role: Role): Promise<{ ok: true }> =>
  post('/api/admin/accounts/role', { userId, role });

export const signOutAccount = (userId: string): Promise<{ ok: true }> =>
  post('/api/admin/accounts/signout', { userId });

export interface AdminRoom {
  zoneId: string;
  name: string;
  kind: string;
  minRole: Role;
  restricted: boolean;
  members: string[];
}

export const listRooms = (): Promise<{ rooms: AdminRoom[]; accounts: Account[] }> =>
  request('/api/admin/rooms');

export const setRoomPolicy = (
  zoneId: string,
  minRole: Role,
  restricted: boolean,
): Promise<{ ok: true }> => post('/api/admin/rooms/policy', { zoneId, minRole, restricted });

export const setRoomMember = (
  zoneId: string,
  userId: string,
  allowed: boolean,
): Promise<{ ok: true }> => post('/api/admin/rooms/members', { zoneId, userId, allowed });
